import {
  EuqSetDataList,
  EuqThreatTypeItem,
} from '../../../types/administrator';

/**
 * The fixed set of EUQ threat types rendered in the Add-in quarantine
 * management table, in display order. The euqSettings response is expected to
 * carry an entry per type, but a given platform's response may omit some — see
 * normalizeThreatTypes.
 */
export const THREAT_TYPES: string[] = [
  'Ransomware',
  'Malicious File',
  'Malicious URL',
  'Phishing',
  'BEC',
  'Spoofing',
  'Spam',
  'Blocked',
  'DLP',
  'UntestedOrUnscannable',
  'Active Content',
  'Suspicious Object',
  'Suspicious Detect',
  'Anomaly',
];

interface ThreatTypeEntry {
  view: number;
  take_action: number;
}
type RawThreatTypeMap =
  | Partial<Record<string, Partial<ThreatTypeEntry> | undefined>>
  | undefined
  | null;

/**
 * Normalize a (possibly partial, undefined, or null) threat_type map from the
 * euqSettings response into a complete map that contains every THREAT_TYPES key
 * with numeric view/take_action values (defaulting to 0).
 *
 * Downstream code indexes every threat type directly — both when building the
 * table rows and when assembling the save payload (onSave/confirmSave read
 * `_viewList['Ransomware'].view` etc.). Normalizing here keeps that code safe
 * when a platform's response omits threat types (e.g. the Gmail add-in reusing
 * this panel) instead of throwing and dropping the whole panel into the error
 * state. Returns a fresh object; the source is never mutated.
 */
export function normalizeThreatTypes(
  rawThreatType: RawThreatTypeMap
): EuqThreatTypeItem {
  const source = rawThreatType ?? {};
  const normalized = THREAT_TYPES.reduce<Record<string, ThreatTypeEntry>>(
    (acc, type) => {
      const entry = source[type] ?? {};
      acc[type] = {
        view: entry.view || 0,
        take_action: entry.take_action || 0,
      };
      return acc;
    },
    {}
  );
  return normalized as EuqThreatTypeItem;
}

/**
 * Build the quarantine-management table rows from a normalized threat_type map,
 * preserving THREAT_TYPES order.
 */
export function buildThreatTypeItems(
  normalizedThreatType: EuqThreatTypeItem
): EuqSetDataList[] {
  const map = normalizedThreatType as unknown as Record<
    string,
    ThreatTypeEntry
  >;
  return THREAT_TYPES.map((type) => ({
    type,
    view: map[type]?.view || 0,
    take_action: map[type]?.take_action || 0,
  }));
}

/**
 * Whether to surface the "configure a Gmail quarantine mailbox first" banner
 * (and disable the panel) on the Gmail add-in tab.
 *
 * Only a SUCCESSFUL response with an empty mailbox list means "not configured".
 * The loading and error states must NOT be treated as "not configured":
 *  - during loading the query has no data yet, so treating it as empty flashes
 *    the banner and disables the panel on every visit, even when a mailbox is
 *    in fact configured;
 *  - on a transient API failure, showing "no mailbox configured" mislabels the
 *    error and hard-disables editing with no way forward.
 */
export function shouldShowGmailMailboxBanner(params: {
  isSuccess: boolean;
  mailBoxList: string[] | undefined | null;
}): boolean {
  return params.isSuccess && (params.mailBoxList ?? []).length === 0;
}
